/**
 * MCC Quote Engine — Core Pricing Logic
 */
import { LABOR_HOURS } from '../data/laborHours.js';
import { MKT } from '../data/marketPricing.js';

export function nemaFromHp(hp) {
  if (!hp || hp === 0) return null;
  if (hp <= 0.5) return 0;
  if (hp <= 10)  return 1;
  if (hp <= 25)  return 3;
  return 5;
}

export function calcBucketCost(starterType, load, ADS) {
  if (!load.hp || load.hp === 0) {
    return { mat: 850, hrs: LABOR_HOURS.feeder_bkt, note: 'Feeder/ctrl bucket' };
  }
  const hp = load.hp;
  if (starterType === 'soft') {
    const ss  = hp <= 3  ? ADS['SS.3HP'].lpp
              : hp <= 15 ? ADS['SS.15HP'].lpp
              : ADS['SS.75HP'].lpp;
    const cb  = hp <= 15 ? ADS['AB.CB.40A'].lpp : ADS['AB.CB.100A'].lpp;
    const hw  = hp <= 3  ? 120 : hp <= 15 ? 280 : 550;
    const hrs = hp <= 3  ? LABOR_HOURS.soft_3hp
              : hp <= 15 ? LABOR_HOURS.soft_15hp
              : LABOR_HOURS.soft_75hp;
    return {
      mat:  Math.round(ss + cb + hw),
      hrs,
      note: hp <= 3  ? 'WEG SSW05'
          : hp <= 15 ? 'MOT VMX-SGY 156A'
          : 'MOT VMX-SGY 302A',
    };
  }
  // VFD
  const vfd = hp <= 3  ? ADS['VFD.3HP'].lpp
            : hp <= 15 ? ADS['VFD.15HP'].lpp
            : ADS['VFD.75HP'].lpp;
  const cb  = hp <= 15 ? ADS['AB.CB.40A'].lpp : ADS['AB.CB.100A'].lpp;
  const hw  = hp <= 3  ? 380 : hp <= 15 ? 750 : 2400;
  const hrs = hp <= 3  ? LABOR_HOURS.vfd_3hp
            : hp <= 15 ? LABOR_HOURS.vfd_15hp
            : LABOR_HOURS.vfd_75hp;
  return {
    mat:  Math.round(vfd + cb + hw),
    hrs,
    note: hp <= 3  ? 'YEC GA800 5.4A'
        : hp <= 15 ? 'YEC GA800 23A'
        : 'YEC GA800 89A',
  };
}

export function calcInfraCost(enclosure, controlVoltage, ADS, numSections) {
  const NS = numSections;
  const rows = [
    { l: `MCC Structure (${NS} sec)`, mat: MKT.structure[enclosure][1] * NS * 0.55, hrs: LABOR_HOURS.structure_section * NS, note: 'Est. 55% mid-mkt' },
    { l: '1600A Bus (65kAIC)',         mat: MKT.bus[1] * NS * 0.50,                hrs: LABOR_HOURS.bus_section * NS,      note: 'Copper bus est.' },
    { l: 'Main 1600AF/AT Breaker',     mat: MKT.mainBreaker[1] * 0.52,             hrs: LABOR_HOURS.main_breaker,          note: 'Est.' },
    { l: 'Surge Protective Device',    mat: ADS['SPD'].lpp * 3,                    hrs: LABOR_HOURS.spd,                   note: ADS['SPD'].p },
    { l: 'Digital Power Meter',        mat: ADS['METER'].lpp * 2,                  hrs: LABOR_HOURS.meter,                 note: ADS['METER'].p },
    { l: '9KVA Transformer + PDP',     mat: ADS['XFMR.9KVA'].lpp * 4,             hrs: LABOR_HOURS.xfmr_9kva,            note: ADS['XFMR.9KVA'].p },
    { l: 'PDP Bucket',                 mat: MKT.pdp[1] * 0.50,                    hrs: 4,                                 note: 'Est.' },
    { l: 'EtherNet/IP Switches (x2)',  mat: ADS['AB.SWITCH.ENIP'].lpp * 2,        hrs: LABOR_HOURS.enip_switch * 2,      note: ADS['AB.SWITCH.ENIP'].p },
    { l: `CPT per Section (x${NS})`,   mat: ADS['XFMR.CPT.1KVA'].lpp * NS * 1.5, hrs: LABOR_HOURS.cpt_section * NS,     note: ADS['XFMR.CPT.1KVA'].p },
  ];
  if (controlVoltage !== '120') {
    rows.push({ l: '24VDC PSU (x2)', mat: ADS['AB.PSU.960W'].lpp * 2, hrs: LABOR_HOURS.psu_24v * 2, note: ADS['AB.PSU.960W'].p });
  }
  return rows;
}

export function generateQuote(config) {
  const {
    starterType, loads, enclosure, controlVoltage,
    laborRate, overheadPct, marginPct, ADS,
  } = config;
  const numSections = Math.max(6, Math.ceil(loads.length / 3) + 2);
  const effectiveRate = laborRate * (1 + overheadPct / 100);
  const buckets = loads.map(load => {
    const mktTbl = MKT[starterType];
    const ns = load.nemaSize;
    const mktPrice = (!load.hp || load.hp === 0)
      ? mktTbl.ctrl[1]
      : (mktTbl[ns <= 0 ? 0 : ns] || mktTbl[0])[1];
    const { mat, hrs, note } = calcBucketCost(starterType, load, ADS);
    const laborCost = Math.round(hrs * effectiveRate);
    const totalCost = mat + laborCost;
    const sellPrice = Math.round(totalCost / (1 - marginPct / 100));
    return { ...load, mktPrice, mat, hrs, laborCost, totalCost, sellPrice, gp: sellPrice - totalCost, note };
  });
  const infraRows = calcInfraCost(enclosure, controlVoltage, ADS, numSections);
  const totalInfraMat   = infraRows.reduce((s, r) => s + r.mat, 0);
  const totalInfraHrs   = infraRows.reduce((s, r) => s + r.hrs, 0);
  const totalInfraLabor = Math.round(totalInfraHrs * effectiveRate);
  const totalInfraCost  = Math.round(totalInfraMat + totalInfraLabor);
  const totalBktHrs = buckets.reduce((s, b) => s + b.hrs, 0);
  const qaHrs       = Math.round((totalBktHrs + totalInfraHrs) * LABOR_HOURS.shop_qa_pct);
  const qaShipLabor = Math.round((qaHrs + LABOR_HOURS.shop_ship_hrs) * effectiveRate);
  const grandMat   = buckets.reduce((s, b) => s + b.mat, 0) + totalInfraMat;
  const grandLabor = buckets.reduce((s, b) => s + b.laborCost, 0) + totalInfraLabor + qaShipLabor;
  const grandCost  = grandMat + grandLabor;
  const sellPrice  = Math.round(grandCost / (1 - marginPct / 100));
  const gp         = sellPrice - grandCost;
  return {
    buckets,
    infraRows,
    numSections,
    totals: {
      materialCost: grandMat,
      laborCost:    grandLabor,
      totalCost:    grandCost,
      sellPrice,
      grossProfit:  gp,
      gpPct:        Math.round((gp / sellPrice) * 100),
    },
  };
}
